'use client';

import React from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';

export interface FormDatePickerProps extends Omit<DatePickerProps<any>, 'label'> {
  /** The label text to display */
  label?: string;
  /** Whether this field is required (if false/undefined, field is optional) */
  required?: boolean;
  /** Whether the field is in edit mode (required indicator only shows in edit mode) */
  isEditMode?: boolean;
  /** Error state for the input */
  error?: boolean;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * A DatePicker wrapper that uses the same label style as FormAutocomplete
 * (string label with Required/Optional, floating outlined label) for consistent
 * row height when placed side-by-side with other form fields.
 *
 * @example
 * // Basic usage
 * <FormDatePicker label="Date" />
 *
 * @example
 * // Required field
 * <FormDatePicker label="Date" required />
 *
 */
export const FormDatePicker: React.FC<FormDatePickerProps> = ({
  label,
  required,
  isEditMode = true,
  error,
  size = 'medium',
  disabled,
  value,
  ...datePickerProps
}) => {
  // Match FormAutocomplete: string label with (Required)/(Optional) so the
  // label floats inside the border and row height matches adjacent fields
  let labelText = label || '';
  if (label && isEditMode) {
    labelText = required ? `${label} (Required)` : `${label} (Optional)`;
  }
  const hasValue = value !== null && value !== undefined;
  return (
    <DatePicker
      {...datePickerProps}
      value={value}
      disabled={disabled}
      slotProps={{
        textField: {
          label: labelText,
          error,
          size,
          fullWidth: true,
          InputLabelProps: {
            shrink: hasValue,
            required: false,
          },
        },
      }}
    />
  );
};

export default FormDatePicker;
